/**
 * GameMode - defines the overall rules and setup for a world instance. Manages player spawning,
 * lifecycle events, and game session state. Marked as transient (only class name is serialized).
 */
import * as THREE from 'three';

import { GameSessionInfo } from '../actors/GameSessionInfo.js';
import { InfoActor } from '../actors/InfoActor.js';
import { Pawn } from '../actors/Pawn.js';
import { PlayerController } from '../actors/PlayerController.js';
import { PlayerInfo } from '../actors/PlayerInfo.js';
import { PlayerStart } from '../actors/PlayerStart.js';
import { GameSessionState } from '../multiplayer/interface.js';
import { NetRuntime } from '../multiplayer/NetRuntime.js';
import { Constructor, EngineClass, getClassOf } from '../systems/ClassRegistry.js';
import { ObjectNamingSystem } from '../systems/ObjectNamingSystem.js';

import type { ActorOptions } from '../actors/Actor.js';
import type { DisconnectReason } from '../multiplayer/interface.js';
import type { JoinParams, PlayerJoinResult } from '../multiplayer/interface.js';
import type {
  ISceneGraphDebugNodeGenerator,
  SceneGraphDebugNode
} from '../utils/SceneGraphUtils.js';
import type { IDumper, ILoader } from '../utils/serialization/factories.js';

export interface GameModeOptions extends ActorOptions {
  /**
   * Async factory function that returns the pawn instance for players.
   * If not specified, defaults to spawning a base Pawn.
   */
  pawnFactory?: () => Promise<Pawn>;

  /**
   * Factory function that returns the GameSessionInfo instance.
   * If not specified, defaults to spawning a base GameSessionInfo.
   */
  gameSessionInfoFactory?: () => GameSessionInfo;

  /**
   * Async factory function that returns the PlayerInfo instance for players.
   * If not specified, defaults to spawning a base PlayerInfo.
   */
  playerInfoFactory?: () => Promise<PlayerInfo>;

  /**
   * Async factory function that returns the PlayerController instance for players.
   * If not specified, defaults to spawning a base PlayerController.
   */
  playerControllerFactory?: () => Promise<PlayerController>;
}

@EngineClass('GameMode')
export class GameMode<TGameModeOptions extends GameModeOptions = GameModeOptions> extends InfoActor implements ISceneGraphDebugNodeGenerator {
  protected gameSessionInfo: GameSessionInfo | null = null;

  /**
   * Server-only: `PlayerInfo` actors for players who disconnected but may reconnect.
   * Keyed by `PlayerInfo.identityToken`.  Not replicated.
   */
  public inactivePlayerInfos: Map<string, PlayerInfo> = new Map();

  protected options: TGameModeOptions = {} as TGameModeOptions;

  constructor() {
    super();
    this.name = ObjectNamingSystem.generateName(this);
    this.setTransient(true);
  }

  public override initialize(options?: TGameModeOptions): void {
    super.initialize(options);
    if (options) {
      this.options = options;
    }
  }

  public override tickPrePhysics(deltaTime: number): void {
    // Skip ticking if not playing (e.g., after endPlay() is called)
    if (!this.isPlaying()) {
      return;
    }
  }

  /**
   * Called during a server-side map transition when the new map declares a different
   * `PlayerInfo` class than the one currently held by the player.
   *
   * @param old - The existing `PlayerInfo` that was preserved across the transition.
   * @param newClass - The `PlayerInfo` subclass that the new `GameMode` expects.
   * @returns A new (or the same) `PlayerInfo` instance to use going forward, or `null` to
   *   keep the existing instance and log a warning. The default implementation returns `null`.
   *
   * @remarks
   * Override this in a subclass to copy relevant fields from `old` into a fresh instance of
   * `newClass`, returning the migrated object. Return `null` to skip migration (the server
   * will keep the old `PlayerInfo` and emit a console warning).
   */
  public migratePlayerInfo(
    _old: PlayerInfo,
    _newClass: Constructor<PlayerInfo>
  ): PlayerInfo | null {
    return null;
  }

  /**
   * Server-side gate called before a player is admitted to the game.
   *
   * @param clientId - The joining client's ID.
   * @param joinParams - Metadata supplied by the client (e.g. password, team preference).
   * @returns `{ allowed: true }` to permit the join, or `{ allowed: false; reason }` to reject.
   *
   * @remarks
   * The default implementation always allows the join.  Override in a subclass to enforce
   * passwords, player caps, team sizes, or any other server-authoritative policy.
   *
   * Recommended usage:
   * ```ts
   * public override canPlayerJoin(_clientId: string, joinParams: JoinParams): PlayerJoinResult {
   *   if (joinParams.password !== 'secret') return { allowed: false, reason: 'Bad password' };
   *   return { allowed: true };
   * }
   * ```
   */
  public canPlayerJoin(_clientId: string, _joinParams: JoinParams): PlayerJoinResult {
    return { allowed: true };
  }

  /**
   * Called on the server when a player disconnects.
   *
   * @param clientId - The client that disconnected.
   * @param pawn - The pawn the player was controlling, or `null` if none.
   * @param reason - Why the player disconnected.
   *
   * @remarks
   * Override to implement custom disconnect handling (e.g., save state, notify other players).
   * The default implementation is a no-op.
   * `PlayerInfo` has already been moved to `inactivePlayerInfos` when this is called.
   */
  public onPlayerDisconnected(_clientId: string, _pawn: Pawn | null, _reason: DisconnectReason): void {
    // No-op — override to handle player disconnects.
  }

  /**
   * Called before a reconnecting player is admitted.
   *
   * @param newClientId - The new client ID assigned to the reconnecting player.
   * @param playerInfo - The preserved `PlayerInfo` from the previous session.
   * @param joinParams - Metadata from the reconnect handshake.
   * @returns `{ allowed: true }` to permit the rejoin, or `{ allowed: false; reason }` to reject.
   *
   * @remarks
   * Override to enforce custom rejoin policies (e.g., block rejoins after a certain time,
   * check for bans, verify identity tokens).  The default allows all rejoins.
   */
  public canPlayerRejoin(_newClientId: string, _playerInfo: PlayerInfo, _joinParams: JoinParams): PlayerJoinResult {
    return { allowed: true };
  }

  /**
   * Single entry point for `PlayerController` creation in all play modes.
   *
   * @param clientId - The client identifier for the joining player.
   *
   * @remarks
   * Calls `spawnPlayerControllerForClient`, sets the net-owning client, adds the controller
   * to the world, and invokes `handleNewPlayer` (which spawns the pawn).
   *
   * - **Standalone mode:** called by `StandaloneNetWorld.doBeginPlay()` once the world is ready.
   * - **Multiplayer server:** called by `ServerAuthorityNetWorld.handlePlayerJoinedOnServer()`;
   *   the net-world applies replication properties on the returned controller after this call.
   *
   * Subclasses may override to add pre/post spawn logic, but should call `super.onPlayerJoined`
   * to preserve the standard flow.
   */
  public async onPlayerJoined(clientId: string): Promise<PlayerController | null> {
    const controller = await this.spawnPlayerControllerForClient(clientId);
    if (!controller) {
      console.warn(`[GameMode] onPlayerJoined: failed to spawn PlayerController for ${clientId}`);
      return null;
    }
    controller.netOwningClientId = clientId;
    this.world!.addActors(controller);
    await this.handleNewPlayer(controller);
    return controller;
  }

  /**
   * Called by the server when a client finishes loading a map.
   *
   * @param controller - The client's `PlayerController`.
   * @param clientId - The client's network ID.
   *
   * @remarks
   * Handles both paths:
   *
   * - **Fresh join** (controller already has a `PlayerInfo`): no-op — pawn was spawned by
   *   `onPlayerJoined → handleNewPlayer` and is already replicated.
   * - **Post-transition re-join** (controller has no `PlayerInfo`): retrieves the preserved
   *   `PlayerInfo` from `GameSessionInfo.activePlayers`, migrates its class if needed, links
   *   it to the controller, then calls `handleNewPlayer` to spawn the pawn.
   *
   * Subclasses may override to add custom post-load logic, but should call `super.onClientMapLoaded`
   * to preserve the standard PlayerInfo-linking and pawn-spawn flow.
   */
  public async onClientMapLoaded(controller: PlayerController, clientId: string): Promise<void> {
    if (controller.getPlayerInfo()) {
      // Fresh join — pawn already exists, nothing to do here.
      return;
    }

    // Post-transition re-join: link preserved PlayerInfo then spawn pawn.
    const gameSessionInfo = this.getGameSessionInfo();
    const preservedInfo = gameSessionInfo?.activePlayers.get(clientId) ?? null;

    if (preservedInfo) {
      // Spawn a fresh PlayerInfo using the factory to determine the expected type
      const playerInfoFactory = this.getPlayerInfoFactory();
      const expectedClass = getClassOf(await playerInfoFactory());

      let playerInfo: PlayerInfo = preservedInfo;

      if (!(preservedInfo instanceof expectedClass)) {
        const migrated = this.migratePlayerInfo(preservedInfo, expectedClass);
        if (migrated) {
          this.world!.removeActors(preservedInfo);
          migrated.clientId = clientId;
          this.world!.addActors(migrated);
          gameSessionInfo!.activePlayers.set(clientId, migrated);
          playerInfo = migrated;
        } else {
          console.warn(`[GameMode] migratePlayerInfo() returned null for ${clientId} — keeping existing PlayerInfo`);
        }
      }

      controller.setPlayerInfo(playerInfo);
    } else {
      // Edge case — no preserved info: spawn a fresh one.
      await controller.spawnPlayerInfo(clientId);
      const newInfo = controller.getPlayerInfo();
      if (newInfo && gameSessionInfo) {
        gameSessionInfo.activePlayers.set(clientId, newInfo);
      }
    }

    await this.handleNewPlayer(controller);
  }

  public async handleNewPlayer(controller: PlayerController): Promise<void> {
    if (this.playerCanRestart(controller)) {
      await this.restartPlayer(controller);
    }
  }

  /**
   * Checks if the player is allowed to restart. Override to implement custom logic
   * (e.g., block restarts during cutscenes or when player is not ready).
   */
  public playerCanRestart(_controller: PlayerController): boolean {
    return true;
  }

  public async restartPlayer(controller: PlayerController): Promise<void> {
    // Find spawn location
    const playerStart = this.findPlayerStart(controller);

    await this.restartPlayerAtPlayerStart(controller, playerStart!);
  }

  public async restartPlayerAtPlayerStart(controller: PlayerController, playerStart: PlayerStart): Promise<void> {
    const clientId = controller.netOwningClientId;
    if (!clientId) {
      console.error('[GameMode] Cannot restart player: controller has no owning client ID');
      return;
    }

    let spawnPosition: THREE.Vector3;
    let spawnRotation: THREE.Euler;

    if (!playerStart) {
      console.warn('[GameMode] No player start found, spawning player at world origin');
      spawnPosition = new THREE.Vector3(0, 0, 0);
      spawnRotation = new THREE.Euler();
    } else {
      spawnPosition = this.adjustSpawnPositionForPlayerStart(playerStart);
      spawnRotation = playerStart.getWorldRotation();
    }

    // Spawn the pawn
    const pawn = await this.spawnPlayerPawnWithTransform(clientId, controller, spawnPosition, spawnRotation);
    if (!pawn) {
      console.error('[GameMode] Failed to spawn pawn for player restart');
      return;
    }

    // Configure replication (networking concerns)
    pawn.netOwningClientId = clientId;

    // Add to world
    this.world!.addActors(pawn);

    // Possess pawn with controller
    controller.possess(pawn);

    console.log(`[GameMode] Restarted player for client ${clientId}`);
  }

  /**
   * Adjusts spawn position if occupied. Override to customize spawn logic.
   */
  protected adjustSpawnPositionForPlayerStart(playerStart: PlayerStart): THREE.Vector3 {
    const originalPosition = playerStart.getWorldPosition();

    // If the player start is unoccupied, use it as-is
    if (this.isPlayerStartUnoccupied(playerStart)) {
      return originalPosition;
    }

    // Try to find an unoccupied offset position
    const maxAttempts = 10;
    const offsetDistance = 2.0; // Distance to offset from the spawn point
    const checkRadius = 1.5; // Radius to check around spawn point

    for (let attempt = 0; attempt < maxAttempts; attempt++) {
      const randomAngle = Math.random() * Math.PI * 2; // Random angle around the spawn point
      const offset = new THREE.Vector3(
        Math.cos(randomAngle) * offsetDistance,
        0,
        Math.sin(randomAngle) * offsetDistance
      );
      const candidatePosition = originalPosition.clone().add(offset);

      // Check if this position is unoccupied
      const allPawns = this.world!.getActors(Pawn);
      let isOccupied = false;

      for (const pawn of allPawns) {
        const pawnPosition = pawn.getWorldPosition();
        const distance = candidatePosition.distanceTo(pawnPosition);

        if (distance < checkRadius) {
          isOccupied = true;
          break;
        }
      }

      if (!isOccupied) {
        console.log(`[GameMode] Found unoccupied offset position after ${attempt + 1} attempts`);
        return candidatePosition;
      }
    }

    // If all attempts failed, return the original position
    console.warn('[GameMode] Failed to find unoccupied offset position after', maxAttempts, 'attempts, using original position');
    return originalPosition;
  }

  public findPlayerStart(_playerController: PlayerController): PlayerStart | null {
    const allPlayerStarts = this.world!.getActors(PlayerStart);

    if (allPlayerStarts.length === 0) {
      return null;
    }

    // Find all unoccupied player starts
    const unoccupiedPlayerStarts = allPlayerStarts.filter(playerStart =>
      this.isPlayerStartUnoccupied(playerStart)
    );

    // If we have unoccupied starts, pick one randomly
    if (unoccupiedPlayerStarts.length > 0) {
      const randomIndex = Math.floor(Math.random() * unoccupiedPlayerStarts.length);
      const selectedStart = unoccupiedPlayerStarts[randomIndex];
      console.log('[GameMode] Found unoccupied player start {}', selectedStart.name);
      return selectedStart;
    }

    // If all are occupied, return a random one (will be offset in adjustSpawnPositionForPlayerStart)
    const randomIndex = Math.floor(Math.random() * allPlayerStarts.length);
    console.log('[GameMode] All player starts occupied, using random one with offset');
    return allPlayerStarts[randomIndex];
  }

  protected isPlayerStartUnoccupied(playerStart: PlayerStart): boolean {
    const position = playerStart.getWorldPosition();
    const physicsEngine = this.getPhysicsEngine();

    if (!physicsEngine) {
      return true; // No physics engine, assume unoccupied
    }

    // Check for nearby pawns using distance check
    // This is more reliable than raycasts for detecting overlapping players
    const checkRadius = 1.5; // Radius to check around spawn point
    const allPawns = this.world!.getActors(Pawn);

    for (const pawn of allPawns) {
      const pawnPosition = pawn.getWorldPosition();
      const distance = position.distanceTo(pawnPosition);

      if (distance < checkRadius) {
        return false; // Occupied by a nearby pawn
      }
    }

    return true; // Unoccupied
  }

  public getPawnFactory(): () => Promise<Pawn> {
    return this.options.pawnFactory ?? (async () => Pawn.create({
      movementComponent: null,
    }));
  }

  public getPlayerInfoFactory(): () => Promise<PlayerInfo> {
    return this.options.playerInfoFactory ?? (async () => PlayerInfo.create());
  }

  public getPlayerControllerFactory(): () => Promise<PlayerController> {
    return this.options.playerControllerFactory ?? (async () => PlayerController.create());
  }

  public getGameSessionInfoFactory(): () => GameSessionInfo {
    return this.options.gameSessionInfoFactory ?? (() => GameSessionInfo.create());
  }

  public async spawnPlayerControllerForClient(_clientId: string): Promise<PlayerController | null> {
    const controllerFactory = this.getPlayerControllerFactory();
    const playerController = await controllerFactory();
    return playerController;
  }

  /**
   * Server-only: Spawns a player pawn at the specified transform. Override to customize.
   */
  public async spawnPlayerPawnWithTransform(
    _clientId: string,
    _playerController: PlayerController,
    spawnPosition: THREE.Vector3,
    spawnRotation: THREE.Euler
  ): Promise<Pawn | null> {
    const pawnFactory = this.getPawnFactory();
    const pawn = await pawnFactory();

    pawn.setWorldPosition(spawnPosition);
    pawn.setWorldRotation(spawnRotation);

    return pawn;
  }

  public getGameSessionInfo(): GameSessionInfo | null {
    return this.gameSessionInfo;
  }

  /**
   * Server-only: Spawns the GameSessionInfo actor. Called during beginPlay.
   */
  protected spawnGameSessionInfo(): void {
    if (!NetRuntime.isServer()) {
      return;
    }

    if (this.gameSessionInfo) {
      console.warn('[GameMode] GameSessionInfo already exists');
      return;
    }

    const sessionInfoFactory = this.getGameSessionInfoFactory();
    console.log('[GameMode] Spawning GameSessionInfo actor');
    this.gameSessionInfo = sessionInfoFactory();
    this.world!.addActors(this.gameSessionInfo);

    // Initialize with current session state if available
    this.initializeGameSessionInfo();
  }

  protected initializeGameSessionInfo(): void {
    if (!this.gameSessionInfo || !this.world) {
      return;
    }

    const netWorld = this.world.netWorld;
    const gameSession = netWorld.getGameSession();

    if (gameSession) {
      this.gameSessionInfo.sessionState = gameSession.getState();
      this.gameSessionInfo.playerCount = gameSession.getPlayerCount();
      this.gameSessionInfo.maxPlayers = gameSession.getMaxPlayers();

      // Transition to WaitingForPlayers if still initializing
      if (this.gameSessionInfo.sessionState === GameSessionState.Initializing) {
        gameSession.setState(GameSessionState.WaitingForPlayers);
        this.gameSessionInfo.sessionState = GameSessionState.WaitingForPlayers;
      }
    }
  }

  /**
   * Generates a unique player name from the client ID. Override for custom naming.
   */
  public generatePlayerName(clientId: string): string {
    const adjectives = ['Swift', 'Brave', 'Mighty', 'Clever', 'Bold', 'Silent', 'Fierce', 'Noble', 'Quick', 'Wise'];
    const nouns = ['Warrior', 'Knight', 'Ranger', 'Mage', 'Rogue', 'Hunter', 'Paladin', 'Monk', 'Druid', 'Bard'];

    // Use clientId to seed random selection for consistency
    const hash = clientId.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0);
    const adjective = adjectives[hash % adjectives.length];
    const noun = nouns[(hash * 7) % nouns.length];
    const number = (hash % 100).toString().padStart(2, '0');

    return `${adjective}${noun}${number}`;
  }

  public override generateDebugNode(): SceneGraphDebugNode | null {
    return {
      title: this.name,
      data: {
        uuid: this.uuid,
        ...this.options
      }
    };
  }

  public override serialize(dumper: IDumper): void {
    dumper.dumpValue('$uuid', this.uuid);
    dumper.dumpProperties(this);
  }

  public override deserialize(loader: ILoader): void {
    (this as any).uuid = loader.loadValue('$uuid') ?? this.uuid;
    loader.loadProperties(this);
  }

  //====================================================================================================

  protected override doBeginPlay(): void {
    super.doBeginPlay();

    this.spawnGameSessionInfo();
  }

  protected override doEndPlay(): void {
    super.doEndPlay();
    this.gameSessionInfo = null;
  }
}
